import { Link } from "react-router-dom";
import { AlertTriangle, X, ExternalLink } from "lucide-react";

/**
 * Modal mostrado cuando el backend devuelve 409 con
 * { detail: { code: "duplicate_customer", existing: {id, code, name, email, phone} } }.
 *
 * Muestra el cliente que ya existe y permite saltar directamente a su ficha
 * o cancelar la operación.
 */
export default function DuplicateCustomerModal({ existing, conflictField, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-amber-500 shadow-hard w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="dup-customer-modal"
      >
        <div className="flex items-start gap-3 px-5 py-4 bg-amber-50 border-b-2 border-amber-500">
          <AlertTriangle size={22} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-black text-base text-amber-900">
              Ya existe un cliente con este email o teléfono.
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              No podemos guardar el cambio porque crearía un duplicado por <strong>{conflictField === "phone" ? "teléfono" : "email"}</strong>.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white" data-testid="dup-close" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Cliente existente
          </div>
          <div className="border border-[#E5E5E0] bg-[#FAFAF8] p-3 space-y-1.5 text-sm">
            <Row label="Nombre" value={existing.name} />
            <Row label="Código" value={existing.code} mono />
            <Row label="Email"  value={existing.email} mono highlight={conflictField === "email"} />
            <Row label="Teléfono" value={existing.phone} mono highlight={conflictField === "phone"} />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#E5E5E0] flex justify-end gap-2 bg-[#FAFAF8]">
          <button
            onClick={onClose}
            data-testid="dup-cancel"
            className="border border-[#111110] hover:bg-white px-3 py-2 text-sm"
          >
            Cancelar
          </button>
          <Link
            to={`/customers/${existing.id}`}
            onClick={onClose}
            data-testid="dup-goto-existing"
            className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold"
          >
            <ExternalLink size={14} /> Ir a ficha existente
          </Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, highlight }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252] w-20 shrink-0">{label}</span>
      <span className={`${mono ? "font-mono text-xs" : "text-sm"} ${highlight ? "bg-amber-100 px-1 font-bold text-amber-900" : "text-[#111110]"}`}>
        {value || "—"}
      </span>
    </div>
  );
}
